import { useState, useRef } from 'react'
import { videoService } from '../../services'
import { useUploadStore } from '../../stores'
import type { VideoVisibility } from '../../types/database.types'
import { Button, Input, Spinner } from '../common'
import { useToast } from '../common/Toast'

interface VideoUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onUploadComplete: (videoId: string, title: string) => void
}

/**
 * VideoUploadModal - Simplified video upload modal for program wizard
 *
 * Features:
 * - Drag and drop file upload
 * - Title (required), Description (optional), Visibility
 * - Integrates with Mux via videoService
 * - Calls onUploadComplete when upload starts (video processes in background)
 */
export function VideoUploadModal({ isOpen, onClose, onUploadComplete }: VideoUploadModalProps) {
  const { startUpload, updateProgress, setFinalizing, completeUpload, setUploadError } = useUploadStore()
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<VideoVisibility>('private')
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetForm = () => {
    setTitle('')
    setDescription('')
    setVisibility('private')
    setFile(null)
    setIsDragging(false)
    setIsSubmitting(false)
  }

  const handleClose = () => {
    if (isSubmitting) return // Don't allow close during submission
    resetForm()
    onClose()
  }

  const validateAndSetFile = (selectedFile: File) => {
    // Validate file type
    if (!selectedFile.type.startsWith('video/')) {
      toast.error('Please select a video file')
      return
    }

    // Validate file size (500MB max)
    const maxSize = 500 * 1024 * 1024
    if (selectedFile.size > maxSize) {
      toast.error('Video file must be smaller than 500MB')
      return
    }

    setFile(selectedFile)

    // Auto-fill title from filename if empty
    if (!title) {
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '')
      const cleanName = nameWithoutExt
        .replace(/[-_]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      setTitle(cleanName)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      validateAndSetFile(selectedFile)
    }
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      validateAndSetFile(droppedFile)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!file) {
      toast.error('Please select a video file')
      return
    }

    if (!title.trim()) {
      toast.error('Please enter a video title')
      return
    }

    setIsSubmitting(true)

    try {
      // Step 1: Get upload URL from our API (creates video record)
      const uploadData = await videoService.createUploadUrl(
        title.trim(),
        description.trim() || undefined,
        visibility
      )

      if (!uploadData) {
        throw new Error('Failed to create upload URL')
      }

      // Step 2: Create XHR for direct Mux upload
      const xhr = new XMLHttpRequest()

      // Register upload in store
      startUpload(
        uploadData.videoId,
        xhr,
        file.name,
        title.trim(),
        null, // category
        null  // tags
      )

      // Set up progress handler
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          updateProgress(uploadData.videoId, percent)
        }
      })

      // Set up completion handler (runs in background)
      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setFinalizing(uploadData.videoId)
          await completeUpload(uploadData.videoId)
        } else {
          setUploadError(uploadData.videoId, `Upload failed: ${xhr.status}`)
        }
      }

      xhr.onerror = () => {
        setUploadError(uploadData.videoId, 'Upload failed - network error')
      }

      // Start the upload
      xhr.open('PUT', uploadData.uploadUrl)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)

      // Notify parent immediately (upload continues in background)
      toast.success('Upload started! Video will be ready shortly.')
      onUploadComplete(uploadData.videoId, title.trim())
      resetForm()
      onClose()
    } catch (error) {
      console.error('Upload failed:', error)
      const message = error instanceof Error ? error.message : 'Failed to start upload'
      toast.error(message)
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Upload New Video</h2>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* File upload zone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video File <span className="text-red-500">*</span>
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  file
                    ? 'border-blue-300 bg-blue-50'
                    : isDragging
                      ? 'border-blue-500 bg-blue-100'
                      : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isSubmitting}
                />
                {file ? (
                  <div>
                    <svg
                      className="w-8 h-8 text-blue-600 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <p className="font-medium text-gray-900 text-sm">{file.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setFile(null)
                      }}
                      className="text-xs text-blue-600 hover:underline mt-1"
                      disabled={isSubmitting}
                    >
                      Choose different file
                    </button>
                  </div>
                ) : (
                  <div>
                    <svg
                      className="w-8 h-8 text-gray-400 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="font-medium text-gray-900 text-sm">
                      Drag and drop or click to browse
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      MP4, MOV, or WebM up to 500MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <Input
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Single Leg Squat"
              required
              disabled={isSubmitting}
            />

            {/* Description (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the exercise..."
                rows={2}
                disabled={isSubmitting}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
              />
            </div>

            {/* Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visibility
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'private' as const, label: 'Private', icon: '🔒' },
                  { value: 'organization' as const, label: 'Org Only', icon: '🏢' },
                  { value: 'public' as const, label: 'Public', icon: '🌐' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setVisibility(option.value)}
                    disabled={isSubmitting}
                    className={`px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                      visibility === option.value
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    } disabled:opacity-50`}
                  >
                    <span className="mr-1">{option.icon}</span>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !file}>
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" />
                    <span className="ml-2">Uploading...</span>
                  </>
                ) : (
                  'Upload & Add to Program'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
